// Read-only permission catalog: all code-enforced permissions grouped by service,
// with a search box that filters across every service. Permissions are code —
// this view never mutates them.
import React, { useMemo, useState } from 'react';
import { Input, Collapse, Empty, Tag, Typography, Space } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import type { PermissionCatalog, CatalogPermission } from './rbacService';

const scopeColor: Record<string, string> = {
  self: 'default',
  team: 'blue',
  tenant: 'geekblue',
  platform: 'gold',
};

const PermissionRow: React.FC<{ p: CatalogPermission }> = ({ p }) => (
  <div
    style={{
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'flex-start',
      gap: 12,
      padding: '8px 0',
      borderBottom: '1px solid rgba(128,128,128,0.12)',
    }}
  >
    <div style={{ minWidth: 0 }}>
      <Typography.Text code copyable={{ text: p.key }}>
        {p.key}
      </Typography.Text>
      {p.description && (
        <div>
          <Typography.Text type="secondary" style={{ fontSize: 13 }}>
            {p.description}
          </Typography.Text>
        </div>
      )}
    </div>
    {p.dataScope && (
      <Tag color={scopeColor[String(p.dataScope)] || 'default'} style={{ marginInlineEnd: 0 }}>
        {String(p.dataScope)}
      </Tag>
    )}
  </div>
);

const PermissionCatalogView: React.FC<{ catalog: PermissionCatalog | null }> = ({ catalog }) => {
  const [q, setQ] = useState('');

  const services = useMemo(() => {
    const term = q.trim().toLowerCase();
    const all = catalog?.services || [];
    if (!term) return all;
    return all
      .map((s) => ({
        ...s,
        permissions: (s.permissions || []).filter(
          (p) =>
            p.key.toLowerCase().includes(term) ||
            (p.description || '').toLowerCase().includes(term) ||
            s.service.toLowerCase().includes(term),
        ),
      }))
      .filter((s) => s.permissions.length > 0);
  }, [catalog, q]);

  const total = useMemo(
    () => (catalog?.services || []).reduce((n, s) => n + (s.permissions?.length || 0), 0),
    [catalog],
  );
  const shown = services.reduce((n, s) => n + s.permissions.length, 0);

  const items = services.map((s) => ({
    key: s.service,
    label: (
      <Space>
        <Typography.Text strong>{s.service}</Typography.Text>
        <Tag>{s.permissions.length}</Tag>
      </Space>
    ),
    children: (
      <div>
        {s.permissions.map((p) => (
          <PermissionRow key={p.key} p={p} />
        ))}
      </div>
    ),
  }));

  return (
    <div>
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
          <Typography.Text type="secondary">
            {q ? `${shown} of ${total}` : `${total}`} permissions across{' '}
            {catalog?.services?.length || 0} services. Permissions are defined in code — this catalog
            is read-only.
          </Typography.Text>
          <Input
            allowClear
            prefix={<SearchOutlined />}
            placeholder="Search permissions…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            style={{ maxWidth: 320 }}
          />
        </div>
        {items.length ? (
          <Collapse
            items={items}
            defaultActiveKey={q ? items.map((i) => i.key) : []}
            {...(q ? { activeKey: items.map((i) => i.key) } : {})}
          />
        ) : (
          <Empty description="No permissions match your search" />
        )}
      </Space>
    </div>
  );
};

export default PermissionCatalogView;
